import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, Check } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { usePublicMp2Catalog } from "@/hooks/usePublicMp2Catalog";
import { useAlacarteCart } from "@/hooks/useAlacarteCart";
import { useDeliveryAreas } from "@/hooks/useDeliveryAreas";
import { useImageFallback } from "@/hooks/useImageFallback";
import { toastCartError } from "@/lib/mp2/cart-toast";
import { cn } from "@/lib/utils";
import {
  listAlacarte,
  getAlacarteCutoffs,
  listBeveragesPublic,
  searchBeveragesPublic,
  listSoupsPublic,
  listSaladsPublic,
  listAddonsPublic,
  type AlacarteListDish,
  type BeverageListItem,
  type SoupListItem,
  type SaladListItem,
  type AddonListItem,
  type MealSlot,
} from "@/lib/public/mp2-alacarte-cart.functions";
import { getAlacarteMenuDishesForDate } from "@/lib/public/alacarte-today-menu.functions";
import { trackCtaClick } from "@/lib/analytics";
import { track } from "@/lib/analytics/events";
import mealBreakfast from "@/assets/meal-breakfast.jpg";
import mealLunch from "@/assets/meal-lunch.jpg";
import mealSnacks from "@/assets/meal-snacks.jpg";
import mealDinner from "@/assets/meal-dinner.jpg";
import mealPlaceholder from "@/assets/meal-placeholder.jpg";
import { DishTagChips } from "@/components/mp2/DishTagChips";

export const Route = createFileRoute("/products")({
  head: () => ({
    meta: [
      { title: "À la carte — Poonam Kapur Healthy Kitchen" },
      {
        name: "description",
        content:
          "Order individual dishes available today from Poonam Kapur Healthy Kitchen — fresh, home-style meals delivered across Mumbai.",
      },
      { property: "og:title", content: "À la carte — Poonam Kapur Healthy Kitchen" },
      {
        property: "og:description",
        content:
          "Order individual dishes available today from Poonam Kapur Healthy Kitchen — fresh, home-style meals delivered across Mumbai.",
      },
      { property: "og:url", content: "https://poonamkapur.com/products" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "À la carte — Poonam Kapur Healthy Kitchen" },
      {
        name: "twitter:description",
        content:
          "Order individual dishes available today from Poonam Kapur Healthy Kitchen — fresh, home-style meals delivered across Mumbai.",
      },
    ],
    links: [{ rel: "canonical", href: "https://poonamkapur.com/products" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "À la carte — Poonam Kapur Healthy Kitchen",
          url: "https://poonamkapur.com/products",
          about: {
            "@type": "Organization",
            name: "Poonam Kapur Healthy Kitchen",
            url: "https://poonamkapur.com",
          },
        }),
      },
    ],
  }),
  component: AlacartePage,
});

const SLOTS = [
  { key: "breakfast", label: "Breakfast" },
  { key: "lunch", label: "Lunch" },
  { key: "snacks", label: "Snacks" },
  { key: "dinner", label: "Dinner" },
] as const;

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const SLOT_PLACEHOLDER: Record<MealSlot, string> = {
  breakfast: mealBreakfast,
  lunch: mealLunch,
  snacks: mealSnacks,
  dinner: mealDinner,
};

function cleanName(name: string) {
  return name.replace(/^\[DEMO\]\s*/i, "").trim();
}
function num(v: number | string | null | undefined) {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? Math.round(n) : null;
}
function formatPrice(paise: number | null) {
  if (paise == null) return null;
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function todayIST(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  return parts; // en-CA => YYYY-MM-DD
}
function formatDateLabel(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dt.getUTCDay()];
  const mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][dt.getUTCMonth()];
  return `${wd}, ${dt.getUTCDate()} ${mo}`;
}

function AlacartePage() {
  const { catalog } = usePublicMp2Catalog();
  const { areas, isLoading: areasLoading } = useDeliveryAreas();
  const cart = useAlacarteCart();

  const [areaId, setAreaId] = useState<string>("");
  const [date, setDate] = useState<string>(todayIST());
  const [autoAdjusted, setAutoAdjusted] = useState<string | null>(null);
  // Hydrate area/date from the persisted cart context ONCE. After that the
  // user (and the cutoffs auto-advance effect) owns these values — re-syncing
  // on every render fights the auto-advance and causes flicker.
  // A persisted date in the past is never restored: it would make every add
  // fail the server cutoff check until the user happened to change the date.
  const hydratedFromCtxRef = useRef(false);
  useEffect(() => {
    if (!hydratedFromCtxRef.current && cart.ctx) {
      hydratedFromCtxRef.current = true;
      setAreaId(cart.ctx.area_id);
      // À la carte is today-only: a persisted date is never restored.
      setDate(todayIST());

      return;
    }
    if (!areaId && areas.length > 0 && areas[0].id) {
      setAreaId(areas[0].id);
    }
  }, [cart.ctx, areas, areaId]);

  const listFn = useServerFn(listAlacarte);
  const cutoffsFn = useServerFn(getAlacarteCutoffs);

  // When area or date changes, check cutoffs. If every slot is closed for the
  // selected date, auto-advance to the earliest next_available_date the server
  // reports so "Add to cart" never returns cutoff_passed_or_no_rule.
  // Cutoffs are time-sensitive: keep the window short and refresh on focus so a
  // tab left open overnight can never act on a cutoff that has since passed.
  const cutoffsQ = useQuery({
    queryKey: ["mp2-alacarte-cutoffs", areaId, date],
    enabled: !!areaId && !!date,
    queryFn: () => cutoffsFn({ data: { area_id: areaId, on_date: date } }),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const d = cutoffsQ.data as { cutoffs?: Array<{ is_open: boolean; next_available_date?: string | null }> } | { error: string } | undefined;
    if (!d || "error" in d || !d.cutoffs) return;
    const anyOpen = d.cutoffs.some((c) => c.is_open);
    if (anyOpen) {
      setAutoAdjusted(null);
      return;
    }
    // À la carte is a today-only service: never advance the date to a future
    // day. The date stays on today and closed slots are simply not orderable.
  }, [cutoffsQ.data]);


  const q = useQuery({
    queryKey: ["mp2-alacarte", areaId, date],
    enabled: !!areaId && !!date,
    queryFn: () => listFn({ data: { area_id: areaId, on_date: date } }),
    staleTime: 60_000,
  });
  const data = q.data as { dishes: AlacarteListDish[] } | { error: string } | undefined;
  const allDishes: AlacarteListDish[] = data && "dishes" in data ? data.dishes : [];


  const [pref, setPref] = useState<string>("all");
  const [selectedSlots, setSelectedSlots] = useState<Record<string, boolean>>({
    breakfast: true,
    lunch: true,
    snacks: true,
    dinner: true,
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<Record<string, number>>({} );
  const [qtyDraft, setQtyDraft] = useState<Record<string, number>>({});
  const [mode, setMode] = useState<"food" | "beverages" | "soups" | "salads" | "addons">("food");

  const preferences = useMemo(
    () =>
      (catalog?.food_preferences ?? [])
        .filter((p) => p.is_active !== false)
        .map((p) => ({ code: p.code, label: p.label })),
    [catalog],
  );

  // À la carte dishes come from the existing Weekly Menu for TODAY only.
  const menuFn = useServerFn(getAlacarteMenuDishesForDate);
  const menuQ = useQuery({
    queryKey: ["alacarte-today-menu", date, pref],
    enabled: !!date,
    queryFn: () =>
      menuFn({ data: { on_date: date, food_preference_code: pref === "all" ? null : pref } }),
    staleTime: 60_000,
  });
  const menuData = menuQ.data as
    | { entries: Array<{ dish_id: string; meal_slot: string }> }
    | { error: string }
    | undefined;
  const menuKeys = useMemo(() => {
    if (!menuData || "error" in menuData) return null; // lookup failed → don't hide the menu
    const s = new Set<string>();
    for (const e of menuData.entries) s.add(`${e.dish_id}|${(e.meal_slot || "").toLowerCase()}`);
    return s;
  }, [menuData]);

  const dishes = useMemo(() => {
    if (!menuKeys) return allDishes;
    return allDishes.filter((d) =>
      menuKeys.has(`${d.id}|${String(d.meal_slot || "").toLowerCase()}`),
    );
  }, [allDishes, menuKeys]);

  const filtered = useMemo(() => {
    return dishes.filter((d) => {
      // Meal slot filter logic is handled in the UI render to keep all slots 
      // available for direct navigation/anchoring if needed, but we apply 
      // it here for strict A La Carte visibility.
      if (!selectedSlots[d.meal_slot]) return false;
      
      if (pref !== "all") {
        const compat = d.preference_compatibility ?? [];
        if (compat.length > 0 && !compat.includes(pref)) return false;
      }
      return true;
    });
  }, [dishes, pref, selectedSlots]);


  const bySlot = useMemo(() => {
    const g: Record<MealSlot, AlacarteListDish[]> = {
      breakfast: [], lunch: [], snacks: [], dinner: [],
    };
    for (const d of filtered) if (g[d.meal_slot]) g[d.meal_slot].push(d);
    return g;
  }, [filtered]);

  const cartQtyByDish = useMemo(() => {
    const m: Record<string, { item_id: string; qty: number }> = {};
    for (const i of cart.snapshot?.items ?? []) {
      if (i.kind === "dish" && i.dish_id) {
        m[i.dish_id] = { item_id: i.id, qty: i.quantity };
      }
    }
    return m;
  }, [cart.snapshot]);

  const canAdd = !!areaId && !!date;

  const applyContext = async () => {
    if (!areaId || !date) return;
    await cart.updateContext({ area_id: areaId, date });
    q.refetch();
  };

  const addToCart = async (d: AlacarteListDish) => {
    if (!canAdd) {
      toast.error("Choose a delivery area and date first.");
      return;
    }
    const qty = Math.max(1, Math.min(10, qtyDraft[d.id] ?? 1));
    // The SERVER cart row is the only authority for what date/area an add is
    // validated against. Comparing just the local context could leave the cart
    // row on an older date (so the same selection is rejected once, then
    // accepted after any unrelated context write) — always reconcile both.
    // Server cart row is authoritative — reconcile before validating an add.
    const sync = await cart.syncContext({ area_id: areaId, date });
    if (sync && typeof sync === "object" && "error" in sync) {
      toastCartError(sync.error);
      return;
    }

    setBusyId(d.id);
    try {
      const res = await cart.addItem(d.id, d.meal_slot as MealSlot, qty);
      if (res && typeof res === "object" && "error" in res) {
        toastCartError(res.error);
      } else {
        toast.success(`Added ${cleanName(d.name)} × ${qty} to cart`);
        setJustAdded((s) => ({ ...s, [d.id]: Date.now() }));
        setTimeout(() => {
          setJustAdded((s) => {
            const n = { ...s };
            delete n[d.id];
            return n;
          });
        }, 1600);
        setQtyDraft((s) => ({ ...s, [d.id]: 1 }));
        trackCtaClick({ action: "alacarte_add_to_cart", location: "catalogue", label: d.slug });
      }
    } catch (err) {
      toastCartError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const changeCartQty = async (item_id: string, next: number) => {
    if (next <= 0) {
      await cart.removeItem(item_id);
      return;
    }
    if (next > 10) return;
    await cart.updateItem(item_id, next);
  };

  // À la carte serves TODAY only — the date control offers today and nothing else.
  const dateOptions = useMemo(() => [todayIST()], []);


  return (
    <SiteLayout>
      <PageHero
        eyebrow="À la carte"
        title="Pick individual dishes for the date you want."
        intro="Choose your delivery area and date, then add dishes to your cart. Availability, meal slot cutoffs and prices are computed live."
      />

      <Section>
        <div className="mb-6 grid gap-3 rounded-2xl border border-border/70 bg-card p-4 sm:grid-cols-[1.4fr_1fr_auto]">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Deliver to
            </span>
            <select
              value={areaId}
              onChange={(e) => setAreaId(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              disabled={areasLoading}
            >
              {areas.length === 0 ? (
                <option value="">No areas configured</option>
              ) : null}
              {areas.map((a) => (
                <option key={a.id} value={a.id ?? ""}>
                  {a.neighbourhood}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Delivery date
            </span>
            <select
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {dateOptions.map((iso) => (
                <option key={iso} value={iso}>
                  {formatDateLabel(iso)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={applyContext}
            disabled={!canAdd}
            className="mt-auto inline-flex h-10 items-center justify-center rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            Set
          </button>
        </div>

        <div className="mb-6 space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-wider">Select Daily Meals</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {SLOTS.map((slot) => (
              <div 
                key={slot.key} 
                className={cn(
                  "flex items-center space-x-3 p-3 rounded-xl border bg-card hover:bg-primary/5 transition-colors cursor-pointer",
                  selectedSlots[slot.key] && "border-primary/50 bg-primary/5"
                )}
                onClick={() => {
                  setSelectedSlots(prev => ({ ...prev, [slot.key]: !prev[slot.key] }));
                }}
              >
                <Checkbox 
                  id={`slot-filter-${slot.key}`} 
                  checked={selectedSlots[slot.key]} 
                  onCheckedChange={(checked: boolean) => setSelectedSlots(prev => ({ ...prev, [slot.key]: !!checked }))}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
                <Label htmlFor={`slot-filter-${slot.key}`} className="font-medium cursor-pointer">{slot.label}</Label>
              </div>
            ))}
          </div>
        </div>

        <div
          role="tablist"
          aria-label="Catalogue category"
          className="mb-4 inline-flex flex-wrap rounded-full border border-border/70 bg-background p-1 text-sm"
          onKeyDown={(e) => {
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            e.preventDefault();
            const order = ["food", "beverages", "soups", "salads", "addons"] as const;
            const idx = order.indexOf(mode);
            const next =
              e.key === "ArrowRight"
                ? order[(idx + 1) % order.length]
                : order[(idx - 1 + order.length) % order.length];
            setMode(next);
          }}
        >
          {(["food", "beverages", "soups", "salads", "addons"] as const).map((m) => {
            const selected = mode === m;
            const label =
              m === "food" ? "Food" : m === "beverages" ? "Beverages" : m === "soups" ? "Soups" : m === "salads" ? "Salads" : "Add-ons";
            return (
              <button
                key={m}
                type="button"
                role="tab"
                id={`catalogue-tab-${m}`}
                aria-selected={selected}
                aria-controls={`catalogue-panel-${m}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setMode(m)}
                className={`rounded-full px-4 py-1.5 font-medium capitalize transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  selected ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {mode === "beverages" ? (
          <div
            role="tabpanel"
            id="catalogue-panel-beverages"
            aria-labelledby="catalogue-tab-beverages"
          >
            <BeveragesPanel
              areaId={areaId}
              date={date}
              canAdd={canAdd}
              cart={cart}
            />
          </div>
        ) : mode === "soups" ? (
          <div role="tabpanel" id="catalogue-panel-soups" aria-labelledby="catalogue-tab-soups">
            <SoupsPanel areaId={areaId} date={date} canAdd={canAdd} cart={cart} />
          </div>
        ) : mode === "salads" ? (
          <div role="tabpanel" id="catalogue-panel-salads" aria-labelledby="catalogue-tab-salads">
            <SaladsPanel areaId={areaId} date={date} canAdd={canAdd} cart={cart} />
          </div>
        ) : mode === "addons" ? (
          <div role="tabpanel" id="catalogue-panel-addons" aria-labelledby="catalogue-tab-addons">
            <AddonsPanel areaId={areaId} date={date} canAdd={canAdd} cart={cart} />
          </div>
        ) : (
        <div
          role="tabpanel"
          id="catalogue-panel-food"
          aria-labelledby="catalogue-tab-food"
        >

        {autoAdjusted && autoAdjusted === date ? (
          <Alert className="mb-4">
            <AlertTitle>Delivery date moved to {formatDateLabel(autoAdjusted)}</AlertTitle>
            <AlertDescription>
              Order cutoffs for earlier dates have already passed. Pick another
              date from the selector if you'd like a later delivery.
            </AlertDescription>
          </Alert>
        ) : null}

        {q.isLoading || areasLoading ? (
          <div className="text-sm text-muted-foreground">Loading today's dishes…</div>
        ) : data && "error" in data ? (
          <Alert>
            <AlertTitle>À la carte isn't available right now</AlertTitle>
            <AlertDescription>
              We couldn't load the à la carte menu ({data.error}). Please check back shortly.
            </AlertDescription>
          </Alert>
        ) : (

          <>
            <div>
              <div className="mb-2 h-px w-10 bg-primary/40" aria-hidden />
              <h2 className="font-display text-3xl font-medium md:text-4xl">
                À la carte menu · {formatDateLabel(date)}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {filtered.length} dishes available. Jump to a section to browse.
              </p>

            </div>

            <nav
              aria-label="Meal slots"
              className="sticky top-16 z-30 -mx-4 mt-4 flex gap-2 overflow-x-auto border-b border-border/60 bg-background/90 px-4 py-3 backdrop-blur md:top-20"
            >
              {SLOTS.map((s) => {
                const n = bySlot[s.key].length;
                return (
                  <a
                    key={s.key}
                    href={`#slot-${s.key}`}
                    aria-disabled={n === 0}
                    className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      n === 0
                        ? "border-border/50 text-muted-foreground/60 pointer-events-none"
                        : "border-border/70 bg-background text-foreground hover:bg-secondary"
                    }`}
                  >
                    {s.label} ({n})
                  </a>
                );
              })}
            </nav>

            {preferences.length > 0 ? (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  Preference
                </span>
                <button
                  type="button"
                  onClick={() => setPref("all")}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    pref === "all"
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border/70 bg-background text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Any
                </button>
                {preferences.map((p) => (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => setPref(p.code)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      pref === p.code
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border/70 bg-background text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            ) : null}

            {filtered.length === 0 ? (
              <Alert className="mt-6">
                <AlertTitle>No à la carte dishes for this date and area</AlertTitle>
                <AlertDescription>
                  Try another date, a different preference, or a different delivery area.
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="mt-8 space-y-12">

              {SLOTS.map((s) => {
                const items = bySlot[s.key];
                if (items.length === 0) return null;
                return (
                  <section 
                    key={s.key} 
                    id={`slot-${s.key}`} 
                    className={cn(
                      "scroll-mt-32 transition-all duration-300",
                      !selectedSlots[s.key] ? "hidden pointer-events-none" : "block opacity-100"
                    )}
                  >
                    <div className="mb-4 flex items-baseline justify-between gap-4">
                      <h3 className="font-display text-2xl font-medium">{s.label}</h3>
                      <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                        {items.length} dish{items.length === 1 ? "" : "es"}
                      </span>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((d) => (
                        <DishCard
                          key={d.id}
                          dish={d}
                          canAdd={canAdd}
                          busy={busyId === d.id}
                          added={!!justAdded[d.id]}
                          qty={qtyDraft[d.id] ?? 1}
                          inCart={cartQtyByDish[d.id]}
                          onQty={(n) => setQtyDraft((s) => ({ ...s, [d.id]: n }))}
                          onAdd={() => addToCart(d)}
                          onCartQty={(next) => {
                            const row = cartQtyByDish[d.id];
                            if (row) void changeCartQty(row.item_id, next);
                          }}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>

            <div className="mt-12 flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center md:p-8">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {cart.itemCount === 0
                  ? "Your cart is empty"
                  : `${cart.itemCount} item${cart.itemCount === 1 ? "" : "s"} in cart · ${formatPrice(cart.totalPaise)}`}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/cart"
                  onClick={() =>
                    trackCtaClick({
                      action: "alacarte_view_cart",
                      location: "alacarte-footer",
                      label: `${cart.itemCount}`,
                    })
                  }
                  className="inline-flex items-center rounded-full bg-primary px-8 py-3 text-sm font-medium text-primary-foreground shadow-sm transition-transform duration-300 hover:-translate-y-0.5 motion-reduce:transform-none"
                >
                  View cart
                </Link>
              </div>
              <p className="text-xs text-muted-foreground">
                Prices, availability and cutoffs are validated by our kitchen before checkout.
              </p>
            </div>
          </>
        )}
        </div>
        )}
      </Section>
    </SiteLayout>
  );
}

function DishCard({
  dish: d,
  canAdd,
  busy,
  added,
  qty,
  inCart,
  onQty,
  onAdd,
  onCartQty,
}: {
  dish: AlacarteListDish;
  canAdd: boolean;
  busy: boolean;
  added: boolean;
  qty: number;
  inCart?: { item_id: string; qty: number };
  onQty: (n: number) => void;
  onAdd: () => void;
  onCartQty: (next: number) => void;
}) {
  const fallback = SLOT_PLACEHOLDER[d.meal_slot] ?? mealPlaceholder;
  const primary = d.image_url && d.image_url.length > 0 ? d.image_url : fallback;
  const { onError } = useImageFallback(fallback);
  const kcal = num(d.calories_kcal);
  const protein = num(d.protein_g);
  const days = (d.available_days ?? []).filter((v) => v >= 0 && v <= 6);
  const price = formatPrice(d.unit_price_paise);
  return (
    <article className="relative flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card">
      <div className="aspect-[4/3] overflow-hidden bg-secondary/40">
        <img
          src={primary}
          alt={d.alt_text ?? cleanName(d.name)}
          loading="lazy"
          onError={onError}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <span>{d.meal_slot}</span>
            {d.is_veg === true ? <span aria-hidden>·</span> : null}
            {d.is_veg === true ? <span>Veg</span> : d.is_veg === false ? <span>Non-veg</span> : null}
          </div>
          {price ? <span className="text-sm font-semibold text-foreground">{price}</span> : null}
        </div>
        <h3 className="line-clamp-2 text-base font-medium">{cleanName(d.name)}</h3>
        {d.description ? (
          <p className="line-clamp-2 text-sm text-muted-foreground">{d.description}</p>
        ) : null}
        <DishTagChips tags={d.display_tags} className="pt-1" />

        {days.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1 pt-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Served</span>
            {days.map((d2) => (
              <Badge key={`day-${d2}`} variant="secondary" className="text-[10px]">{DAY_LABELS[d2]}</Badge>
            ))}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-1 pt-1">
          {kcal != null ? <Badge variant="outline" className="text-[10px]">{kcal} kcal</Badge> : null}
          {protein != null ? <Badge variant="outline" className="text-[10px]">{protein}g protein</Badge> : null}
          {(d.allergens ?? []).map((a) => (

            <Badge key={`al-${a}`} variant="outline" className="text-[10px]">contains {a}</Badge>
          ))}
        </div>

        <div className="mt-auto pt-3">
          {inCart ? (
            <div className="flex items-center justify-between gap-3 rounded-full border border-primary/40 bg-primary/5 px-2 py-1.5">
              <span className="pl-2 text-xs font-medium text-primary">In cart</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  onClick={() => onCartQty(inCart.qty - 1)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/70 bg-background text-foreground hover:bg-secondary"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[2ch] text-center text-sm font-semibold tabular-nums">{inCart.qty}</span>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  disabled={inCart.qty >= 10}
                  onClick={() => onCartQty(inCart.qty + 1)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/70 bg-background text-foreground hover:bg-secondary disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-2 py-1">
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  disabled={qty <= 1}
                  onClick={() => onQty(Math.max(1, qty - 1))}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-foreground hover:bg-secondary disabled:opacity-40"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[2ch] text-center text-sm font-medium tabular-nums">{qty}</span>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  disabled={qty >= 10}
                  onClick={() => onQty(Math.min(10, qty + 1))}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-foreground hover:bg-secondary disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <button
                type="button"
                onClick={onAdd}
                disabled={!canAdd || busy || d.unit_price_paise == null}
                className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-medium shadow-sm transition-all disabled:opacity-50 ${
                  added
                    ? "bg-emerald-600 text-white"
                    : "bg-primary text-primary-foreground hover:-translate-y-0.5"
                }`}
              >
                {added ? (
                  <>
                    <Check className="h-4 w-4" /> Added
                  </>
                ) : busy ? (
                  "Adding…"
                ) : (
                  <>
                    <ShoppingBag className="h-4 w-4" /> Add to cart
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function BeveragesPanel({
  areaId,
  date,
  canAdd,
  cart,
}: {
  areaId: string;
  date: string;
  canAdd: boolean;
  cart: ReturnType<typeof useAlacarteCart>;
}) {
  const listFn = useServerFn(listBeveragesPublic);
  const searchFn = useServerFn(searchBeveragesPublic);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<Record<string, number>>({});
  const [qtyDraft, setQtyDraft] = useState<Record<string, number>>({});

  // Debounce search input to avoid a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const listQ = useQuery({
    queryKey: ["bev-list", areaId, date],
    enabled: !!areaId && !!date && debouncedSearch.length === 0,
    queryFn: () => listFn({ data: { area_id: areaId, on_date: date } }),
    staleTime: 60_000,
  });
  const searchQ = useQuery({
    queryKey: ["bev-search", areaId, date, debouncedSearch],
    enabled: !!areaId && !!date && debouncedSearch.length > 0,
    queryFn: () => searchFn({ data: { area_id: areaId, on_date: date, q: debouncedSearch } }),
    staleTime: 30_000,
  });

  useEffect(() => {
    track("bev_catalog_viewed", { surface: "products_tab" });
  }, []);

  useEffect(() => {
    if (debouncedSearch.length >= 2) track("bev_search", { query_length: debouncedSearch.length });
  }, [debouncedSearch]);

  const src = debouncedSearch.length > 0 ? searchQ.data : listQ.data;
  const data = src as { beverages: BeverageListItem[] } | { error: string } | undefined;
  const items: BeverageListItem[] = data && "beverages" in data ? data.beverages : [];
  const isLoading = debouncedSearch.length > 0 ? searchQ.isLoading : listQ.isLoading;

  const grouped = useMemo(() => {
    const g = new Map<string, { name: string; items: BeverageListItem[] }>();
    for (const b of items) {
      const key = b.category_slug ?? "uncategorised";
      const label = b.category_name ?? "Other";
      if (!g.has(key)) g.set(key, { name: label, items: [] });
      g.get(key)!.items.push(b);
    }
    return Array.from(g.entries());
  }, [items]);

  const cartQtyByBev = useMemo(() => {
    const m: Record<string, { item_id: string; qty: number }> = {};
    for (const i of cart.snapshot?.items ?? []) {
      if (i.kind === "beverage" && i.beverage_id) m[i.beverage_id] = { item_id: i.id, qty: i.quantity };
    }
    return m;
  }, [cart.snapshot]);

  const addBev = async (b: BeverageListItem) => {
    if (!canAdd) {
      toast.error("Choose a delivery area and date first.");
      return;
    }
    // Server cart row is authoritative — reconcile before validating an add.
    const sync = await cart.syncContext({ area_id: areaId, date });
    if (sync && typeof sync === "object" && "error" in sync) {
      toastCartError(sync.error);
      return;
    }
    const qty = Math.max(1, Math.min(10, qtyDraft[b.id] ?? 1));
    setBusyId(b.id);
    try {
      const res = await cart.addBeverage(b.id, qty);
      if (res && typeof res === "object" && "error" in res) {
        toastCartError(res.error);
      } else {
        toast.success(`Added ${cleanName(b.name)} × ${qty} to cart`);
        setJustAdded((s) => ({ ...s, [b.id]: Date.now() }));
        setTimeout(() => setJustAdded((s) => { const n = { ...s }; delete n[b.id]; return n; }), 1500);
        track("bev_add_to_cart", { beverage_id: b.id, beverage_slug: b.slug, surface: "products_tab" });
        setQtyDraft((s) => { const n = { ...s }; delete n[b.id]; return n; });
      }
    } finally {
      setBusyId(null);
    }
  };

  const changeCartQty = async (item_id: string, next: number) => {
    if (next <= 0) { await cart.removeItem(item_id); return; }
    if (next > 10) return;
    await cart.updateItem(item_id, next);
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            Search beverages
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nimbu pani, lassi, coffee…"
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            aria-label="Search beverages"
          />
        </label>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading beverages…</div>
      ) : data && "error" in data ? (
        <Alert>
          <AlertTitle>Beverages aren't available right now</AlertTitle>
          <AlertDescription>We couldn't load the beverage list ({data.error}).</AlertDescription>
        </Alert>
      ) : items.length === 0 ? (
        <Alert>
          <AlertTitle>No beverages match</AlertTitle>
          <AlertDescription>Try another search or a different delivery date.</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-10">
          {grouped.map(([key, group]) => (
            <section key={key}>
              <div className="mb-3 flex items-baseline justify-between gap-4">
                <h3 className="font-display text-2xl font-medium">{group.name}</h3>
                <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                  {group.items.length} item{group.items.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {group.items.map((b) => (
                  <BeverageCard
                    key={b.id}
                    bev={b}
                    canAdd={canAdd}
                    busy={busyId === b.id}
                    added={!!justAdded[b.id]}
                    inCart={cartQtyByBev[b.id]}
                    qty={qtyDraft[b.id] ?? 1}
                    onQty={(next) => setQtyDraft((s) => ({ ...s, [b.id]: next }))}
                    onAdd={() => addBev(b)}
                    onCartQty={(next) => {
                      const row = cartQtyByBev[b.id];
                      if (row) void changeCartQty(row.item_id, next);
                    }}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function BeverageCard({
  bev: b,
  canAdd,
  busy,
  added,
  inCart,
  qty,
  onQty,
  onAdd,
  onCartQty,
}: {
  bev: BeverageListItem;
  canAdd: boolean;
  busy: boolean;
  added: boolean;
  inCart?: { item_id: string; qty: number };
  qty: number;
  onQty: (next: number) => void;
  onAdd: () => void;
  onCartQty: (next: number) => void;
}) {
  const fallback = mealPlaceholder;
  const primary = b.image_url && b.image_url.length > 0 ? b.image_url : fallback;
  const { onError } = useImageFallback(fallback);
  const price = formatPrice(b.unit_price_paise);
  const meta = [b.size_label, b.serving_temperature, b.serving_unit].filter(Boolean).join(" · ");
  const outOfStock = b.availability_status === "out_of_stock";
  const kcal = num(b.calories_kcal as number | string | null);
  const protein = num(b.protein_g as number | string | null);
  const sugar = num(b.sugar_g as number | string | null);
  return (
    <article className="relative flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card">
      <div className="aspect-[4/3] overflow-hidden bg-secondary/40">
        <img src={primary} alt={b.alt_text ?? cleanName(b.name)} loading="lazy" onError={onError} className="h-full w-full object-cover" />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Beverage{b.is_vegan ? " · vegan" : b.is_veg ? " · veg" : ""}
          </div>
          {price ? <span className="text-sm font-semibold">{price}</span> : null}
        </div>
        <h3 className="line-clamp-2 text-base font-medium">{cleanName(b.name)}</h3>
        {meta ? <p className="text-xs text-muted-foreground">{meta}</p> : null}
        {b.description ? <p className="line-clamp-2 text-sm text-muted-foreground">{b.description}</p> : null}
        <div className="flex flex-wrap gap-1 pt-1">
          {kcal != null ? <Badge variant="outline" className="text-[10px]">{kcal} kcal</Badge> : null}
          {protein != null ? <Badge variant="outline" className="text-[10px]">{protein}g protein</Badge> : null}
          {sugar != null ? <Badge variant="outline" className="text-[10px]">{sugar}g sugar</Badge> : null}
          {(b.allergens ?? []).slice(0, 2).map((a) => (
            <Badge key={`bal-${a}`} variant="outline" className="text-[10px]">contains {a}</Badge>
          ))}
        </div>
        {b.availability_status === "low_stock" ? (
          <Badge variant="outline" className="w-fit text-[10px]">Low stock</Badge>
        ) : null}
        <div className="mt-auto pt-3">
          {inCart ? (
            <div className="flex items-center justify-between gap-3 rounded-full border border-primary/40 bg-primary/5 px-2 py-1.5">
              <span className="pl-2 text-xs font-medium text-primary">In cart</span>
              <div className="flex items-center gap-1">
                <button type="button" aria-label="Decrease" onClick={() => onCartQty(inCart.qty - 1)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/70 bg-background hover:bg-secondary">
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[2ch] text-center text-sm font-semibold tabular-nums">{inCart.qty}</span>
                <button type="button" aria-label="Increase" disabled={inCart.qty >= 10}
                  onClick={() => onCartQty(inCart.qty + 1)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/70 bg-background hover:bg-secondary disabled:opacity-40">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 rounded-full border border-border/70 bg-background px-1 py-1">
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  disabled={qty <= 1}
                  onClick={() => onQty(Math.max(1, qty - 1))}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary disabled:opacity-40"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[2ch] text-center text-sm font-medium tabular-nums">{qty}</span>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  disabled={qty >= 10}
                  onClick={() => onQty(Math.min(10, qty + 1))}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <button type="button" onClick={onAdd}
                disabled={!canAdd || busy || outOfStock || b.unit_price_paise == null}
                className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-medium shadow-sm transition-all disabled:opacity-50 ${
                  added ? "bg-emerald-600 text-white" : "bg-primary text-primary-foreground hover:-translate-y-0.5"
                }`}>
                {outOfStock ? "Out of stock" : added ? (<><Check className="h-4 w-4" /> Added</>) : busy ? "Adding…" : (<><ShoppingBag className="h-4 w-4" /> Add to cart</>)}
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

/* ---------------- Soups & Salads panels (mirror beverages) ---------------- */

type SimpleItem = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  image_url: string | null;
  alt_text: string | null;
  serving_size: string | null;
  serving_unit: string | null;
  serving_temperature?: string | null;
  is_veg: boolean | null;
  is_vegan: boolean | null;
  allergens: string[] | null;
  calories_kcal: number | string | null;
  protein_g: number | string | null;
  sugar_g?: number | string | null;
  availability_status: string;
  unit_price_paise: number | null;
  category_slug: string | null;
  category_name: string | null;
};

function SoupsPanel({
  areaId, date, canAdd, cart,
}: { areaId: string; date: string; canAdd: boolean; cart: ReturnType<typeof useAlacarteCart> }) {
  const listFn = useServerFn(listSoupsPublic);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<Record<string, number>>({});
  const [qtyDraft, setQtyDraft] = useState<Record<string, number>>({});
  const q = useQuery({
    queryKey: ["soup-list", areaId, date],
    enabled: !!areaId && !!date,
    queryFn: () => listFn({ data: { area_id: areaId, on_date: date } }),
    staleTime: 60_000,
  });
  const data = q.data as { soups: SoupListItem[] } | { error: string } | undefined;
  const items = data && "soups" in data ? data.soups : [];
  const cartQty = useMemo(() => {
    const m: Record<string, { item_id: string; qty: number }> = {};
    for (const i of cart.snapshot?.items ?? []) {
      if (i.kind === "soup" && i.soup_id) m[i.soup_id] = { item_id: i.id, qty: i.quantity };
    }
    return m;
  }, [cart.snapshot]);
  const add = async (s: SoupListItem) => {
    if (!canAdd) { toast.error("Choose a delivery area and date first."); return; }
    const sync = await cart.syncContext({ area_id: areaId, date });
    if (sync && typeof sync === "object" && "error" in sync) {
      toastCartError(sync.error);
      return;
    }
    const qty = Math.max(1, Math.min(10, qtyDraft[s.id] ?? 1));
    setBusyId(s.id);
    try {
      const res = await cart.addSoup(s.id, qty);
      if (res && typeof res === "object" && "error" in res) {
        toastCartError(res.error);
      } else {
        toast.success(`Added ${cleanName(s.name)} × ${qty} to cart`);
        setJustAdded((x) => ({ ...x, [s.id]: Date.now() }));
        setTimeout(() => setJustAdded((x) => { const n = { ...x }; delete n[s.id]; return n; }), 1500);
        setQtyDraft((x) => { const n = { ...x }; delete n[s.id]; return n; });
        track("bev_add_to_cart", { surface: "products_tab", kind: "soup", slug: s.slug });
      }
    } finally { setBusyId(null); }
  };
  const changeCartQty = async (item_id: string, next: number) => {
    if (next <= 0) return void cart.removeItem(item_id);
    if (next > 10) return;
    await cart.updateItem(item_id, next);
  };
  return (
    <SimpleCatalogueGrid
      items={items as unknown as SimpleItem[]}
      isLoading={q.isLoading}
      errorText={data && "error" in data ? data.error : null}
      emptyTitle="No soups for this date and area"
      canAdd={canAdd}
      busyId={busyId}
      justAdded={justAdded}
      qtyDraft={qtyDraft}
      onQty={(id, n) => setQtyDraft((s) => ({ ...s, [id]: n }))}
      onAdd={(it) => add(it as unknown as SoupListItem)}
      cartQty={cartQty}
      onCartQty={changeCartQty}
      eyebrow="Soup"
    />
  );
}

function SaladsPanel({
  areaId, date, canAdd, cart,
}: { areaId: string; date: string; canAdd: boolean; cart: ReturnType<typeof useAlacarteCart> }) {
  const listFn = useServerFn(listSaladsPublic);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<Record<string, number>>({});
  const [qtyDraft, setQtyDraft] = useState<Record<string, number>>({});
  const q = useQuery({
    queryKey: ["salad-list", areaId, date],
    enabled: !!areaId && !!date,
    queryFn: () => listFn({ data: { area_id: areaId, on_date: date } }),
    staleTime: 60_000,
  });
  const data = q.data as { salads: SaladListItem[] } | { error: string } | undefined;
  const items = data && "salads" in data ? data.salads : [];
  const cartQty = useMemo(() => {
    const m: Record<string, { item_id: string; qty: number }> = {};
    for (const i of cart.snapshot?.items ?? []) {
      if (i.kind === "salad" && i.salad_id) m[i.salad_id] = { item_id: i.id, qty: i.quantity };
    }
    return m;
  }, [cart.snapshot]);
  const add = async (s: SaladListItem) => {
    if (!canAdd) { toast.error("Choose a delivery area and date first."); return; }
    const sync = await cart.syncContext({ area_id: areaId, date });
    if (sync && typeof sync === "object" && "error" in sync) {
      toastCartError(sync.error);
      return;
    }
    const qty = Math.max(1, Math.min(10, qtyDraft[s.id] ?? 1));
    setBusyId(s.id);
    try {
      const res = await cart.addSalad(s.id, qty);
      if (res && typeof res === "object" && "error" in res) {
        toastCartError(res.error);
      } else {
        toast.success(`Added ${cleanName(s.name)} × ${qty} to cart`);
        setJustAdded((x) => ({ ...x, [s.id]: Date.now() }));
        setTimeout(() => setJustAdded((x) => { const n = { ...x }; delete n[s.id]; return n; }), 1500);
        setQtyDraft((x) => { const n = { ...x }; delete n[s.id]; return n; });
        track("bev_add_to_cart", { surface: "products_tab", kind: "salad", slug: s.slug });
      }
    } finally { setBusyId(null); }
  };
  const changeCartQty = async (item_id: string, next: number) => {
    if (next <= 0) return void cart.removeItem(item_id);
    if (next > 10) return;
    await cart.updateItem(item_id, next);
  };
  return (
    <SimpleCatalogueGrid
      items={items as unknown as SimpleItem[]}
      isLoading={q.isLoading}
      errorText={data && "error" in data ? data.error : null}
      emptyTitle="No salads for this date and area"
      canAdd={canAdd}
      busyId={busyId}
      justAdded={justAdded}
      qtyDraft={qtyDraft}
      onQty={(id, n) => setQtyDraft((s) => ({ ...s, [id]: n }))}
      onAdd={(it) => add(it as unknown as SaladListItem)}
      cartQty={cartQty}
      onCartQty={changeCartQty}
      eyebrow="Salad"
    />
  );
}

function AddonsPanel({
  areaId, date, canAdd, cart,
}: { areaId: string; date: string; canAdd: boolean; cart: ReturnType<typeof useAlacarteCart> }) {
  const listFn = useServerFn(listAddonsPublic);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<Record<string, number>>({});
  const [qtyDraft, setQtyDraft] = useState<Record<string, number>>({});
  const q = useQuery({
    queryKey: ["addon-list", areaId, date],
    enabled: !!areaId && !!date,
    queryFn: () => listFn({ data: { area_id: areaId, on_date: date } }),
    staleTime: 60_000,
  });
  const data = q.data as { addons: AddonListItem[] } | { error: string } | undefined;
  const items = data && "addons" in data ? data.addons : [];
  const cartQty = useMemo(() => {
    const m: Record<string, { item_id: string; qty: number }> = {};
    for (const i of cart.snapshot?.items ?? []) {
      if (i.kind === "addon" && i.addon_id) m[i.addon_id] = { item_id: i.id, qty: i.quantity };
    }
    return m;
  }, [cart.snapshot]);
  const add = async (s: AddonListItem) => {
    if (!canAdd) { toast.error("Choose a delivery area and date first."); return; }
    const sync = await cart.syncContext({ area_id: areaId, date });
    if (sync && typeof sync === "object" && "error" in sync) {
      toastCartError(sync.error);
      return;
    }
    const qty = Math.max(1, Math.min(10, qtyDraft[s.id] ?? 1));
    setBusyId(s.id);
    try {
      const res = await cart.addAddon(s.id, qty);
      if (res && typeof res === "object" && "error" in res) {
        toastCartError(res.error);
      } else {
        toast.success(`Added ${cleanName(s.name)} × ${qty} to cart`);
        setJustAdded((x) => ({ ...x, [s.id]: Date.now() }));
        setTimeout(() => setJustAdded((x) => { const n = { ...x }; delete n[s.id]; return n; }), 1500);
        setQtyDraft((x) => { const n = { ...x }; delete n[s.id]; return n; });
        track("bev_add_to_cart", { surface: "products_tab", kind: "addon", slug: s.slug });
      }
    } finally { setBusyId(null); }
  };
  const changeCartQty = async (item_id: string, next: number) => {
    if (next <= 0) return void cart.removeItem(item_id);
    if (next > 10) return;
    await cart.updateItem(item_id, next);
  };
  return (
    <SimpleCatalogueGrid
      items={items as unknown as SimpleItem[]}
      isLoading={q.isLoading}
      errorText={data && "error" in data ? data.error : null}
      emptyTitle="No add-ons for this date and area"
      canAdd={canAdd}
      busyId={busyId}
      justAdded={justAdded}
      qtyDraft={qtyDraft}
      onQty={(id, n) => setQtyDraft((s) => ({ ...s, [id]: n }))}
      onAdd={(it) => add(it as unknown as AddonListItem)}
      cartQty={cartQty}
      onCartQty={changeCartQty}
      eyebrow="Add-on"
    />
  );
}

function SimpleCatalogueGrid({
  items, isLoading, errorText, emptyTitle, canAdd, busyId, justAdded, qtyDraft,
  onQty, onAdd, cartQty, onCartQty, eyebrow,
}: {
  items: SimpleItem[];
  isLoading: boolean;
  errorText: string | null;
  emptyTitle: string;
  canAdd: boolean;
  busyId: string | null;
  justAdded: Record<string, number>;
  qtyDraft: Record<string, number>;
  onQty: (id: string, n: number) => void;
  onAdd: (it: SimpleItem) => void;
  cartQty: Record<string, { item_id: string; qty: number }>;
  onCartQty: (item_id: string, next: number) => void;
  eyebrow: string;
}) {
  const grouped = useMemo(() => {
    const g = new Map<string, { name: string; items: SimpleItem[] }>();
    for (const b of items) {
      const key = b.category_slug ?? "uncategorised";
      const label = b.category_name ?? "Other";
      if (!g.has(key)) g.set(key, { name: label, items: [] });
      g.get(key)!.items.push(b);
    }
    return Array.from(g.entries());
  }, [items]);
  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (errorText) return (
    <Alert><AlertTitle>Not available right now</AlertTitle><AlertDescription>{errorText}</AlertDescription></Alert>
  );
  if (items.length === 0) return (
    <Alert><AlertTitle>{emptyTitle}</AlertTitle><AlertDescription>Try another date or delivery area.</AlertDescription></Alert>
  );
  return (
    <div className="space-y-10">
      {grouped.map(([key, group]) => (
        <section key={key}>
          <div className="mb-3 flex items-baseline justify-between gap-4">
            <h3 className="font-display text-2xl font-medium">{group.name}</h3>
            <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
              {group.items.length} item{group.items.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {group.items.map((it) => (
              <SimpleItemCard
                key={it.id}
                item={it}
                eyebrow={eyebrow}
                canAdd={canAdd}
                busy={busyId === it.id}
                added={!!justAdded[it.id]}
                qty={qtyDraft[it.id] ?? 1}
                inCart={cartQty[it.id]}
                onQty={(n) => onQty(it.id, n)}
                onAdd={() => onAdd(it)}
                onCartQty={(next) => { const row = cartQty[it.id]; if (row) void onCartQty(row.item_id, next); }}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function SimpleItemCard({
  item: b, eyebrow, canAdd, busy, added, qty, inCart, onQty, onAdd, onCartQty,
}: {
  item: SimpleItem; eyebrow: string; canAdd: boolean; busy: boolean; added: boolean;
  qty: number; inCart?: { item_id: string; qty: number };
  onQty: (n: number) => void; onAdd: () => void; onCartQty: (next: number) => void;
}) {
  const fallback = mealPlaceholder;
  const primary = b.image_url && b.image_url.length > 0 ? b.image_url : fallback;
  const { onError } = useImageFallback(fallback);
  const price = formatPrice(b.unit_price_paise);
  const meta = [b.serving_size, b.serving_temperature, b.serving_unit].filter(Boolean).join(" · ");
  const outOfStock = b.availability_status === "out_of_stock";
  const kcal = num(b.calories_kcal);
  const protein = num(b.protein_g);
  const sugar = num(b.sugar_g ?? null);
  return (
    <article className="relative flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card">
      <div className="aspect-[4/3] overflow-hidden bg-secondary/40">
        <img src={primary} alt={b.alt_text ?? cleanName(b.name)} loading="lazy" onError={onError} className="h-full w-full object-cover" />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {eyebrow}{b.is_vegan ? " · vegan" : b.is_veg ? " · veg" : ""}
          </div>
          {price ? <span className="text-sm font-semibold">{price}</span> : null}
        </div>
        <h3 className="line-clamp-2 text-base font-medium">{cleanName(b.name)}</h3>
        {meta ? <p className="text-xs text-muted-foreground">{meta}</p> : null}
        {b.description ? <p className="line-clamp-2 text-sm text-muted-foreground">{b.description}</p> : null}
        <div className="flex flex-wrap gap-1 pt-1">
          {kcal != null ? <Badge variant="outline" className="text-[10px]">{kcal} kcal</Badge> : null}
          {protein != null ? <Badge variant="outline" className="text-[10px]">{protein}g protein</Badge> : null}
          {sugar != null ? <Badge variant="outline" className="text-[10px]">{sugar}g sugar</Badge> : null}
          {(b.allergens ?? []).slice(0, 2).map((a) => (
            <Badge key={`al-${a}`} variant="outline" className="text-[10px]">contains {a}</Badge>
          ))}
        </div>
        {b.availability_status === "low_stock" ? (
          <Badge variant="outline" className="w-fit text-[10px]">Low stock</Badge>
        ) : null}
        <div className="mt-auto pt-3">
          {inCart ? (
            <div className="flex items-center justify-between gap-3 rounded-full border border-primary/40 bg-primary/5 px-2 py-1.5">
              <span className="pl-2 text-xs font-medium text-primary">In cart</span>
              <div className="flex items-center gap-1">
                <button type="button" aria-label="Decrease" onClick={() => onCartQty(inCart.qty - 1)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/70 bg-background hover:bg-secondary">
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[2ch] text-center text-sm font-semibold tabular-nums">{inCart.qty}</span>
                <button type="button" aria-label="Increase" disabled={inCart.qty >= 10}
                  onClick={() => onCartQty(inCart.qty + 1)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/70 bg-background hover:bg-secondary disabled:opacity-40">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 rounded-full border border-border/70 bg-background px-1 py-1">
                <button type="button" aria-label="Decrease quantity" disabled={qty <= 1}
                  onClick={() => onQty(Math.max(1, qty - 1))}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary disabled:opacity-40">
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[2ch] text-center text-sm font-medium tabular-nums">{qty}</span>
                <button type="button" aria-label="Increase quantity" disabled={qty >= 10}
                  onClick={() => onQty(Math.min(10, qty + 1))}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary disabled:opacity-40">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <button type="button" onClick={onAdd}
                disabled={!canAdd || busy || outOfStock || b.unit_price_paise == null}
                className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-medium shadow-sm transition-all disabled:opacity-50 ${
                  added ? "bg-emerald-600 text-white" : "bg-primary text-primary-foreground hover:-translate-y-0.5"
                }`}>
                {outOfStock ? "Out of stock" : added ? (<><Check className="h-4 w-4" /> Added</>) : busy ? "Adding…" : (<><ShoppingBag className="h-4 w-4" /> Add to cart</>)}
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
